import Big from 'big.js';
import { DateTime } from 'luxon';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import * as buttons from 'smg/buttons';
import * as hyejoo from 'smg/customer-api/hyejoo';
import * as session from 'smg/customer-api/session';
import * as models from 'smg/models';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { PageLoader } from '../account';
import * as carts from '../carts';
import { Relref } from '../hugo';
import * as payments from '../payments';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

export default async function main() {
    if (await session.Redirect(undefined, Relref('/login'))) {
        return;
    }

    const sess = await session.Session();

    const b = new Batch();

    const carriers = b.get<hyejoo.Carriers>('/carriers', {
        params: {
            'data': 'name,link',
        },
    }).then(resp => {
        const m = new Map<number, models.Carrier>();
        for (const carrier of resp.data.carriers) {
            if (carrier.id) {
                m.set(carrier.id, carrier);
            }
        }
        return m;
    });

    const lineItems = b.get<hyejoo.LineItems>('/v2/line-items', {
        params: {
            'data': 'name,pluralName,product,shipping',
        },
    }).then(resp => {
        const m = new Map<string, models.LineItem>();
        for (const lineItem of resp.data.lineItems) {
            if (lineItem.id) {
                m.set(lineItem.id, lineItem);
            }
        }
        return m;
    });

    void b.Exec();

    const pl: PageLoader<models.Order> = new PageLoader<models.Order>({
        get: () => axios.get<hyejoo.Orders>('/orders', {
            params: {
                'sort': pl.Sort(),
                'max': pl.CurrentPageSize(),
                'data': 'invoiceNumber,created,orderLineItems,orderProofs,orderShippingOptions,orderPayments,total,paid,balance,totalSansTax,orderStatusID,key',
                'p': pl.CurrentPage(),
                'q': pl.Search(),
            },
        }).then(resp => ({ rows: resp.data.orders, nextPage: resp.data.nextPage })),
        draw: async order => {
            const el = template.Clone('#order-template');

            const invoiceNumber = el.querySelector('.order-invoice-number') as HTMLElement;
            invoiceNumber.textContent = String(order.invoiceNumber);

            const date = el.querySelector('.order-date') as HTMLElement;
            date.textContent = DateTime.fromISO(order.created || '').toLocaleString(DateTime.DATE_SHORT);

            const total = Big(order.total || 0);
            const totalSansTax = Big(order.totalSansTax || 0);
            let shipping = Big(0);
            for (const ol of order.orderLineItems ?? []) {
                if (!ol.lineItemID) {
                    continue;
                }

                const lineItem = (await lineItems).get(ol.lineItemID || '');
                if (!lineItem?.shipping) {
                    continue;
                }

                shipping = shipping.add(Big(ol.quantity || 0).mul(Big(ol.price || 0)));
            }
            const balance = Big(order.balance || 0);

            const tax = total.sub(totalSansTax);
            const subtotal = totalSansTax.sub(shipping);

            const subtotalEls = el.querySelectorAll('.order-subtotal');
            subtotalEls.forEach(el => el.textContent = DollarFormat(subtotal));

            const shippingEls = el.querySelectorAll('.order-shipping');
            shippingEls.forEach(el => el.textContent = DollarFormat(shipping));

            const taxEls = el.querySelectorAll('.order-tax');
            taxEls.forEach(el => el.textContent = DollarFormat(tax));

            const totalEls = el.querySelectorAll('.order-total');
            totalEls.forEach(el => el.textContent = DollarFormat(total));

            const balanceEls = el.querySelectorAll('.order-balance');
            balanceEls.forEach(el => el.textContent = DollarFormat(balance));

            const balanceContainers = el.querySelectorAll('.order-balance-container');
            const makePayment = el.querySelector('.order-make-payment') as HTMLElement;
            if (balance.gt('0.05')) {
                balanceContainers.forEach(el => el.classList.add('text-danger'));
                makePayment.addEventListener('click', () => payments.PromptPayment(sess, [order.id || ''], balance, false).then(() => pl.Load()));
            } else {
                makePayment.parentElement?.removeChild(makePayment);
            }

            const reorder = el.querySelector('.order-reorder') as HTMLButtonElement;
            reorder.addEventListener('click', () => {
                buttons.Start(reorder);
                void carts.AddCartItem({ orderID: order.key ?? '' }).then(() => {
                    reorder.classList.remove('btn-primary', 'btn-success', 'text-white');
                    reorder.classList.add('btn-outline-success');

                    reorder.querySelector('i')?.classList.remove('fa-shopping-cart');
                    reorder.querySelector('i')?.classList.add('fa-check');
                }).finally(() => buttons.Stop(reorder));
            });

            const invoice = el.querySelector('.order-invoice') as HTMLAnchorElement;
            invoice.href = `${window.apiRoot}/orders/${order.id || ''}/invoice`;

            const viewOrder = el.querySelector('.view-order') as HTMLAnchorElement;
            viewOrder.href = `${Relref('/order')}?id=${order.id || ''}`;

            const proofsContainer = el.querySelector('.order-proofs-container') as HTMLElement;
            const proofsParent = el.querySelector('.order-proofs-parent') as HTMLElement;
            if (order.orderProofs?.length) {
                for (const op of order.orderProofs) {
                    const p = op.proof;
                    if (!p) {
                        continue;
                    }

                    const urls = GetProofURLs(window.cloudFront, p.file || '', p.path || '');

                    const el = template.Clone('#order-proof-template') as HTMLAnchorElement;

                    const a = el.querySelector('a') as HTMLAnchorElement;
                    a.href = urls.main;
                    a.setAttribute('data-fancybox', order.id || '');

                    const img = a.querySelector('img') as HTMLImageElement;
                    img.src = urls.thumb;
                    img.alt = op.name || '';

                    a.appendChild(img);

                    proofsParent.appendChild(a);
                }
            } else {
                proofsContainer.parentElement?.removeChild(proofsContainer);
            }

            let shipmentsCount = 0;
            let delivered = false;
            const trackingParent = el.querySelector('.order-tracking-parent') as HTMLElement;
            if (order.orderShippingOptions?.length) {
                for (const orderShippingOption of order.orderShippingOptions) {
                    if (orderShippingOption.orderShippingOptionShipments?.length) {
                        for (const orderShippingOptionShipment of orderShippingOption.orderShippingOptionShipments) {
                            const shipment = orderShippingOptionShipment.shipment;
                            if (!shipment) {
                                continue;
                            }

                            if (shipment.delivered) {
                                delivered = true;
                            }

                            if (shipmentsCount++) {
                                trackingParent.appendChild(document.createTextNode(', '));
                            }

                            const carrier = (await carriers).get(shipment.carrierID || 0);

                            if (carrier) {
                                const a = document.createElement('a') as HTMLAnchorElement;
                                const carrierId = (await carriers).get(shipment.carrierID || 0);
                                if (carrierId) {
                                    a.href = carrierId.link?.replace('%%', shipment.trackingNumber || '') || '';
                                }
                                a.textContent = shipment.trackingNumber || '';

                                trackingParent.appendChild(a);
                            } else {
                                trackingParent.appendChild(document.createTextNode(shipment.trackingNumber || ''));
                            }
                        }
                    }
                }
            }
            if (!shipmentsCount) {
                const trackingContainer = el.querySelector('.order-tracking-container');
                trackingContainer?.parentElement?.removeChild(trackingContainer);
            }

            const orderLineItemStages: models.OrderLineItemStage[] = [];

            if (order.orderLineItems?.length) {
                const qtys: Record<string, number> = {};
                for (const ol of order.orderLineItems) {
                    if (ol.orderLineItemStages?.length) {
                        orderLineItemStages.push(...ol.orderLineItemStages);
                    }

                    if (!ol.quantity) {
                        continue;
                    }

                    const lineItem = (await lineItems).get(ol.lineItemID || '');

                    if (!(ol.lineItem || lineItem?.product)) {
                        continue;
                    }

                    const name = ol.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                    qtys[name] = (qtys[name] || 0) + ol.quantity;
                }

                const summary = el.querySelector('.order-summary') as HTMLElement;
                summary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
            }

            const orderStatusEl = el.querySelector('.order-status') as HTMLElement;
            let orderStatus: string | undefined;
            switch (true) {
            case delivered:
                orderStatus = 'Delivered';
                break;
            case shipmentsCount !== 0:
                orderStatus = 'Shipped';
                break;
            case orderLineItemStages.length !== 0:
                orderLineItemStages?.sort((a, b) => DateTime.fromISO(b.created || '').valueOf() - DateTime.fromISO(a.created || '').valueOf());
                orderStatus = orderLineItemStages[0].stage?.name || '';
                break;
            case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED:
                orderStatus = 'In production';
                break;
            case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_UNCONFIRMED:
                orderStatus = 'Preparing for production';
                break;
            case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_ORDER:
                orderStatus = 'Order received';
                break;
            }

            orderStatusEl.textContent = orderStatus || '';

            return el;
        },
    });

    pl.Load();
}